import React, { useState } from 'react'
import styled from '@emotion/styled'
import { useDispatch } from 'react-redux'
import { useSelector } from 'hooks/redux'
import PropTypes from 'prop-types'
import { Header, Divider, Paragraph, Icon, Button } from 'components/patterns'
import strings from 'utils/strings'
import SingleEditor from 'components/vehicles/PowerTakeOff/FragmentEditor/SingleEditor'
import { getIsEditing } from 'selectors/navigator'
import {
  updatePtoTemplateFragmentValue,
  updatePtoTemplateFragmentFromDefault,
} from 'actions/powerTakeOff'
import {
  getTemplateById,
  getGroupTemplateFragmentsWithBounds,
  getDomainByTemplate,
} from 'selectors/powerTakeOff'
import GroupEditor from 'components/vehicles/PowerTakeOff/FragmentEditor/GroupEditor'
import {
  ModalBody,
  ModalButton,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalMainContainer,
  ModalSmall,
} from 'components/patterns/Modal'
import IconAlert from 'components/patterns/Icon/components/Icon__Alert'

export default function Editor({ refNo, templateId }) {
  const dispatch = useDispatch()
  const [showAddModal, updateShowAddModal] = useState(false)
  const [showRemoveModal, updateShowRemoveModal] = useState(false)
  const isEditing = useSelector(getIsEditing)
  const template = useSelector(getTemplateById, { templateId })
  const { [refNo]: fragment } = template.fragments
  const canEdit = true && isEditing

  const { groupId, name, fromDefaultString: isNotInTemplate } = fragment

  const { name: domainName } =
    useSelector(getDomainByTemplate, {
      templateId,
      domainId: fragment.domain,
    }) || {}

  const { fragments, enables, enabledBy, lowerBound, upperBound } = useSelector(
    getGroupTemplateFragmentsWithBounds,
    {
      groupId,
      templateId,
    }
  )

  const handleUpdateValue = (fragment, newValue) => {
    dispatch(updatePtoTemplateFragmentValue(templateId, fragment, newValue))
  }

  const handleAddToTemplate = () => {
    updateShowAddModal(false)
    dispatch(updatePtoTemplateFragmentFromDefault(templateId, refNo, false))
  }

  const handleRemoveFromTemplate = () => {
    updateShowRemoveModal(false)
    dispatch(updatePtoTemplateFragmentFromDefault(templateId, refNo, true))
  }

  const handleShowAddToTemplate = () => {
    if (isNotInTemplate) {
      updateShowAddModal(true)
      updateShowRemoveModal(false)
    } else {
      updateShowRemoveModal(true)
      updateShowAddModal(false)
    }
  }

  return (
    <StyledFragmentEditor>
      <Paragraph>{domainName}</Paragraph>
      <Divider collapseMargins />
      <div style={{ marginBottom: 16 }}>
        <Header type="h3">{fragment.name}</Header>
      </div>
      {isNotInTemplate && (
        <>
          <Paragraph
            size="small"
            style={{ marginBottom: 16, lineHeight: 1.5 }}
            color="slatePrimary"
          >
            <StyledIcon
              icon="alert_info_i_round_solid"
              fill="slatePrimary"
              width={20}
              height={20}
            />{' '}
            {strings.pto_template_refno_not_included_in_template}
          </Paragraph>
        </>
      )}
      {!groupId && (
        <SingleEditor
          fragment={fragment}
          readOnly={!canEdit}
          updateValue={handleUpdateValue}
          disabled={isNotInTemplate}
        />
      )}
      {groupId && (
        <GroupEditor
          {...{
            fragments,
            enables,
            enabledBy,
            lowerBound,
            upperBound,
            updateValue: handleUpdateValue,
            templateId,
            readOnly: !canEdit,
          }}
        />
      )}
      {isEditing && (
        <StyledButtonContainer>
          <StyledDivider />
          <Button
            onClick={handleShowAddToTemplate}
            type={isNotInTemplate ? 'secondary' : 'danger'}
            style={{ float: 'right' }}
          >
            {isNotInTemplate && <>{strings.pto_template_add_to_template}</>}
            {!isNotInTemplate && (
              <>{strings.pto_template_remove_from_template}</>
            )}
          </Button>
        </StyledButtonContainer>
      )}
      {showAddModal && (
        <ConfirmModal
          onConfirm={handleAddToTemplate}
          onClose={() => updateShowAddModal(false)}
          title={strings.pto_template_add_this_setting_to_template}
          message={strings.pto_template_you_are_adding_fragment_to_template(
            name
          )}
          confirmButtonText={strings.pto_template_add_to_template}
        />
      )}
      {showRemoveModal && (
        <ConfirmModal
          onConfirm={handleRemoveFromTemplate}
          onClose={() => updateShowRemoveModal(false)}
          title={strings.pto_template_remove_this_setting_from_template}
          message={strings.pto_template_you_are_removing_fragment_from_template(
            name
          )}
          confirmButtonText={strings.pto_template_remove_from_template}
          confirmButtonType="danger"
        />
      )}
    </StyledFragmentEditor>
  )
}

Editor.propTypes = {
  refNo: PropTypes.string,
  templateId: PropTypes.string,
}

const StyledFragmentEditor = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden auto;
  &&& .ant-divider {
    margin-bottom: 8px;
  }
`

const StyledIcon = styled(Icon)`
  &&&& {
    vertical-align: bottom;
  }
`

const ConfirmModal = ({
  onClose,
  onConfirm,
  title,
  message,
  confirmButtonText,
  confirmButtonType = 'primary',
}) => (
  <ModalSmall data-testid="confirm-modal">
    <ModalHeader data-testid="modal-header">
      <Header type="h3">
        <IconAlert />
        {title}
      </Header>
      <ModalCloseButton onClick={onClose} />
    </ModalHeader>
    <ModalBody data-testid="modal-body">
      <ModalMainContainer>
        <ModalContent>{message}</ModalContent>
      </ModalMainContainer>
    </ModalBody>
    <ModalFooter data-testid="modal-footer">
      <ModalButton type="secondary" onClick={onClose}>
        {strings.cancel}
      </ModalButton>
      <ModalButton type={confirmButtonType} onClick={onConfirm}>
        {confirmButtonText}
      </ModalButton>
    </ModalFooter>
  </ModalSmall>
)

ConfirmModal.propTypes = {
  onConfirm: PropTypes.func,
  onClose: PropTypes.func,
  title: PropTypes.string,
  message: PropTypes.string,
  confirmButtonText: PropTypes.string,
  confirmButtonType: PropTypes.string,
}

const StyledButtonContainer = styled.div`
  margin-top: auto;
`

const StyledDivider = styled(Divider)`
  &&&& {
    width: calc(100% + 40px);
    margin-left: -20px;
  }
`
